import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { ADMIN, AUTH } from "@/constants/testIds";
import {
  LayoutDashboard, Search, Server, Bookmark, Sparkles, Star,
  Database, GitBranch, Calculator, Hammer, Images, Mail, Users,
  CheckSquare, StickyNote, FileText, FolderOpen, BarChart3,
  Globe, Settings, LogOut, TrendingUp, Wrench, Users2, Radio,
  Menu, X,
} from "lucide-react";

const groups = [
  {
    title: "Overview",
    items: [
      { to: "/admin", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
    ],
  },
  {
    title: "Discovery",
    items: [
      { to: "/admin/search-engine", key: "search-engine", label: "Search Engine", icon: Search },
      { to: "/admin/saved-searches", key: "saved-searches", label: "Saved Searches", icon: Bookmark },
      { to: "/admin/new-finds", key: "new-finds", label: "New Finds", icon: Sparkles },
      { to: "/admin/sources", key: "sources", label: "Source Manager", icon: Server },
      { to: "/admin/pipelines", key: "pipelines", label: "Data Pipelines", icon: Radio },
      { to: "/admin/watchlist", key: "watchlist", label: "Watchlist", icon: Star },
    ],
  },
  {
    title: "Deals",
    items: [
      { to: "/admin/properties", key: "properties", label: "Property Database", icon: Database },
      { to: "/admin/pipeline", key: "pipeline", label: "Deal Pipeline", icon: GitBranch },
      { to: "/admin/calculator", key: "calculator", label: "Offer Calculator", icon: Calculator },
      { to: "/admin/portfolio", key: "portfolio", label: "Portfolio Performance", icon: TrendingUp },
      { to: "/admin/refurb-library", key: "refurb-library", label: "Refurb Library", icon: Wrench },
      { to: "/admin/projects", key: "projects", label: "Renovation Projects", icon: Hammer },
      { to: "/admin/case-studies", key: "case-studies", label: "Before & After CMS", icon: Images },
    ],
  },
  {
    title: "CRM",
    items: [
      { to: "/admin/leads", key: "leads", label: "Leads", icon: Mail },
      { to: "/admin/tasks", key: "tasks", label: "Tasks", icon: CheckSquare },
      { to: "/admin/notes", key: "notes", label: "Notes", icon: StickyNote },
    ],
  },
  {
    title: "Library",
    items: [
      { to: "/admin/media", key: "media", label: "Media Library", icon: FolderOpen },
      { to: "/admin/reports", key: "reports", label: "Reports", icon: BarChart3 },
      { to: "/admin/cms", key: "cms", label: "Website CMS", icon: Globe },
      { to: "/admin/team", key: "team", label: "Team", icon: Users2 },
      { to: "/admin/settings", key: "settings", label: "Settings", icon: Settings },
    ],
  },
];

function NavGroups({ onNavigate, idPrefix = "" }) {
  return groups.map((g) => (
    <div key={g.title} className="mt-2 first:mt-0">
      <p className="px-6 mb-1 text-[0.7rem] text-ivory/40">{g.title}</p>
      {g.items.map((it) => (
        <NavLink
          key={it.key}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={`${idPrefix}${ADMIN.sidebarLink(it.key)}`}
          className={({ isActive }) => `admin-link ${isActive ? "active" : ""}`}
        >
          <it.icon size={15} strokeWidth={1.5} />
          <span>{it.label}</span>
        </NavLink>
      ))}
    </div>
  ));
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && user === false) nav("/admin/login", { replace: true });
  }, [loading, user, nav]);

  useEffect(() => { window.scrollTo({ top: 0 }); setMenuOpen(false); }, [loc.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  if (loading || !user) {
    return <div className="admin-dark min-h-screen bg-charcoal-950 text-ivory/50 flex items-center justify-center">Loading…</div>;
  }

  return (
    <div className="admin-dark min-h-screen bg-charcoal-950 text-ivory flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-[260px] flex-col border-r border-charcoal-600 bg-charcoal-950 sticky top-0 h-screen">
        <div className="px-6 py-4 border-b border-ruby/15 shrink-0">
          <Link to="/admin" className="inline-flex items-center gap-3">
            <LogoMark size={20} className="text-ruby" />
            <span className="font-serif text-base text-ivory">Rubys<span className="text-ruby">.</span>Group</span>
          </Link>
          <p className="mt-1 text-[0.7rem] text-ivory/50">Operating System</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          <NavGroups />
        </nav>
        <div className="px-6 py-2.5 border-t border-charcoal-600 text-[0.7rem] text-ivory/50 shrink-0">
          <p className="text-ivory/80 truncate">{user.email}</p>
          <button
            onClick={logout}
            data-testid={AUTH.logout}
            className="mt-1 flex items-center gap-2 text-ivory/60 hover:text-gold text-[0.65rem] uppercase tracking-[0.18em]"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* mobile top bar */}
        <div className="md:hidden sticky top-0 z-40 bg-charcoal-950/95 backdrop-blur-xl border-b border-ruby/15 px-4 py-3 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2" data-testid="admin-mobile-logo">
            <LogoMark size={22} className="text-ruby" />
            <span className="font-serif text-lg text-ivory">Rubys<span className="text-ruby">.</span>Group</span>
          </Link>
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="admin-mobile-menu-btn"
            aria-label="Open admin menu"
            className="inline-flex items-center gap-2 text-ivory/80 hover:text-ivory p-2 -mr-2"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>

        {/* mobile nav drawer */}
        {menuOpen && (
          <div className="md:hidden fixed inset-0 z-50" data-testid="admin-mobile-drawer">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setMenuOpen(false)}
              data-testid="admin-mobile-drawer-backdrop"
            />
            <aside className="absolute inset-y-0 left-0 w-[300px] max-w-[85vw] bg-charcoal-950 border-r border-charcoal-600 flex flex-col animate-fade-in">
              <div className="px-6 py-4 border-b border-ruby/15 shrink-0 flex items-center justify-between">
                <Link to="/admin" onClick={() => setMenuOpen(false)} className="inline-flex items-center gap-3">
                  <LogoMark size={20} className="text-ruby" />
                  <span className="font-serif text-base text-ivory">Rubys<span className="text-ruby">.</span>Group</span>
                </Link>
                <button
                  onClick={() => setMenuOpen(false)}
                  data-testid="admin-mobile-drawer-close"
                  aria-label="Close admin menu"
                  className="text-ivory/60 hover:text-ivory p-2 -mr-2"
                >
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto py-2">
                <NavGroups onNavigate={() => setMenuOpen(false)} idPrefix="mobile-" />
              </nav>
              <div className="px-6 py-3 border-t border-charcoal-600 text-[0.7rem] text-ivory/50 shrink-0">
                <p className="text-ivory/80 truncate">{user.email}</p>
                <button
                  onClick={logout}
                  data-testid="admin-mobile-logout"
                  className="mt-1.5 flex items-center gap-2 text-ivory/60 hover:text-gold text-[0.65rem] uppercase tracking-[0.18em] py-1"
                >
                  <LogOut size={12} /> Sign out
                </button>
              </div>
            </aside>
          </div>
        )}

        <Outlet />
      </div>
    </div>
  );
}
